import { Button, Form, Popconfirm, Spin, Table, Tooltip } from "antd";
import Dropdown from "./Dropdown";
import React, { useState, useRef, useEffect } from "react";
import ExportHandler from "./export/ExportHandlerEmployeeSkillSet";
import ExportButton from "./export/ExportButton";
import filterPageLabels from "./searchPageLabels.json";
import { useSelector } from "react-redux";
import { fetchDataForFilterDropdown, searchEmployeesByCompetences, exportAllEmployeesByCompetences, fetchDataForDropdown, fetchEmployeesByCompetences,} from "../../services/Service";
import columns from "../utils/FilterConfig";

const EmployeeSkillSet = () => {
  const { labels, placeholders, buttons, other, tooltip } = filterPageLabels;

  // Create the form instance
  const [form] = Form.useForm();
  const [isSearchDisabled, setIsSearchDisabled] = useState(true);
  const token = useSelector((state) => state.auth.token);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const [searchResults, setSearchResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(false);

  const initialFilters = {
    primarySkill: "",
    experience: "",
    status: "",
    location: "",
    certification: "",
    primarySkillExperience: "",
  };
  const filterRef = useRef(null);
  // Function to reset form fields
  const handleReset = () => {
    form.resetFields();
    setIsSearchDisabled(true);
    setFilters(initialFilters);
    setSelectedRowKeys(new Set());
    setPage(1);
    setPageSize(10);
    fetchEmployeeData();
    // Focus the search bar after reset
    if (filterRef.current) {
      filterRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    checkSearchButtonStatus();
    if (!searchPerformed) {
      fetchEmployeeData();
    }
    if (searchResults.length === 0) {
      setLoading(false);
    } else {
      if (searchResults.length > 10) {
        setLoading(true);
        setTimeout(() => setLoading(false), 1000);
      } else {
        setLoading(false);
      }
    }
  }, [filters]);

  const fetchEmployeeData = async () => {
    try {
      const data = await fetchEmployeesByCompetences(page - 1, pageSize, token);
      if (data && Array.isArray(data.content)) {
        setSearchResults(data.content);
        setTotalRecords(data.content.length);
        setSearchPerformed(false);
      } else {
        console.error("Expected an array but got:", data);

        setSearchResults([]);
        setTotalRecords(0);
        setSearchPerformed(false);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleFilterChange = (key, value, form) => {
    if (form && form.validateFields) {
      form.validateFields([key]);
    }
    setFilters({ ...filters, [key]: value });
  };

  const checkSearchButtonStatus = () => {
    // Check if at least one field is filled and valid
    const isFormValid = Object.values(filters).some(
      (value) => value !== "" && value !== null
    );
    // Enable the search button only if all fields are valid
    if (isFormValid) {
      setIsSearchDisabled(false);
    } else {
      setIsSearchDisabled(true);
    }
  };
  const tableRef = useRef(null);

  const handleSearch = async () => {
    try {
      await form.validateFields();
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Prepare query params
      const queryParams = {
        primarySkill:
          filters.primarySkill && filters.primarySkill !== "All"
            ? filters.primarySkill
            : undefined,
        overallExp:
          filters.experience && filters.experience !== "All"
            ? filters.experience
            : undefined,
        status:
          filters.status && filters.status !== "All"
            ? filters.status
            : undefined,
        location:
          filters.location && filters.location !== "All"
            ? filters.location
            : undefined,
        certification:
          filters.certification && filters.certification !== "All"
            ? filters.certification
            : undefined,
        expInPrimarySkill:
          filters.primarySkillExperience &&
          filters.primarySkillExperience !== "All"
            ? filters.primarySkillExperience
            : undefined,
      };

      // Remove undefined or empty values from queryParams
      Object.keys(queryParams).forEach(
        (key) =>
          (queryParams[key] === undefined || queryParams[key] === "") &&
          delete queryParams[key]
      );

      const result = await searchEmployeesByCompetences(queryParams, token);

      setSearchResults(result || []);
      setTotalRecords(result ? result.length : 0);
      setSearchPerformed(true);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleExport = async () => {
    if (!searchResults || searchResults.length === 0) {
      console.error("No search results to export.");
      return;
    }

    const exportAll = selectedRowKeys.size === 0;
    const isSearchFiltered = exportAll && searchPerformed;

    if (isSearchFiltered) {
      // Export search results directly
      ExportHandler({ searchResults: searchResults, selectedRowKeys: [] })();
      setSelectedRowKeys(new Set());
      return;
    }

    try {
      const allRecords = await exportAllEmployeesByCompetences(token);
      let rowsToExport;

      if (exportAll) {
        // Export all records
        rowsToExport = allRecords;
      } else {
        // Export selected records

        rowsToExport = allRecords.filter((row) => selectedRowKeys.has(row.uid));
      }

      ExportHandler({
        searchResults: rowsToExport,
        selectedRowKeys: exportAll ? [] : Array.from(selectedRowKeys),
      })();
      setSelectedRowKeys(new Set());
    } catch (error) {
      console.error(
        `Error fetching records for ${exportAll ? "all" : "selected"} export:`,
        error
      );
    }
  };
  //Dynamic Page Size Options for pagination
  const getPageSizeOptions = () => {
    const defaultOptions = ["10", "20", "50", "100"];
    const validOptions = defaultOptions.filter(
      (size) => Number(size) <= Math.max(totalRecords, 10)
    );
    if (!validOptions.includes(totalRecords.toString())) {
      validOptions.push(totalRecords.toString());
    }
    return validOptions;
  };
  const exportTooltip =
    searchResults.length === 0 && searchPerformed
      ? filterPageLabels.tooltip.noDataToExport
      : selectedRowKeys.size === 0
      ? tooltip.exportAllSuccess.replace("{count}", totalRecords)
      : tooltip.exportSelectedSuccess.replace("{count}", selectedRowKeys.size);

  const rowSelection = {
    selectedRowKeys: Array.from(selectedRowKeys),
    onChange: (keys) => {
      setSelectedRowKeys(new Set(keys));
    },
    preserveSelectedRowKeys: true,
    columnWidth: 40,
  };

  return (
    <>
      <div className="bg-white h-[340px] text-white flex flex-col p-4 rounded-md shadow-lg max-w-[1050px] mx-auto">
        <div
          ref={filterRef}
          className="mb-4 text-2xl font-semibold text-center border bg-[var(--color-primary)]  rounded-tl-[25px] rounded-tr-[25px]"
        >
          <span className="inline-block h-8 align-middle mt-2">
            Employee Skill Set Filter
          </span>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="globalInfo" className="mt-[-6px]">
            <span className="text-gray-500 italic">{other.infoMessage}</span>
          </Form.Item>
          <div className="grid grid-cols-3 gap-x-6 mt-[-22px]">
            <Form.Item
              name="primarySkill"
              label={labels.primarySkill}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() => fetchDataForDropdown("primarySkills")}
                placeholder={placeholders.primarySkill}
                onChange={(value) =>
                  handleFilterChange(
                    "primarySkill",
                    value === "ALL" ? "All" : value
                  )
                }
                includeAll={true}
              />
            </Form.Item>
            <Form.Item
              name="experience"
              label={labels.experience}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() => fetchDataForFilterDropdown("TotalExperience")}
                placeholder={placeholders.experience}
                onChange={(value) =>
                  handleFilterChange(
                    "experience",
                    value === "ALL" ? "All" : value
                  )
                }
                includeAll={true}
              />
            </Form.Item>
            <Form.Item
              name="status"
              label={labels.status}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() => fetchDataForDropdown("employmentStatus")}
                placeholder={placeholders.status}
                onChange={(value) =>
                  handleFilterChange("status", value === "ALL" ? "All" : value)
                }
                includeAll={true}
              />
            </Form.Item>
            <Form.Item
              name="location"
              label={labels.location}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() => fetchDataForDropdown("locations")}
                placeholder={placeholders.location}
                onChange={(value) =>
                  handleFilterChange(
                    "location",
                    value === "ALL" ? "All" : value
                  )
                }
                includeAll={true}
              />
            </Form.Item>
            <Form.Item
              name="certification"
              label={labels.certification}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() => fetchDataForDropdown("certifications")}
                placeholder={placeholders.certification}
                onChange={(value) =>
                  handleFilterChange(
                    "certification",
                    value === "ALL" ? "All" : value
                  )
                }
                includeAll={true}
              />
            </Form.Item>
            <Form.Item
              name="primarySkillExperience"
              label={labels.primarySkillExperience}
              labelCol={{ className: "text-sm font-medium" }}
            >
              <Dropdown
                fetchData={() =>
                  fetchDataForFilterDropdown("PrimaryExperience")
                }
                placeholder={placeholders.primarySkillExperience}
                onChange={(value) =>
                  handleFilterChange(
                    "primarySkillExperience",
                    value === "ALL" ? "All" : value
                  )
                }
                includeAll={true}
              />
            </Form.Item>
          </div>
          <div className="flex justify-center gap-4">
            <Tooltip
              title={
                isSearchDisabled
                  ? tooltip.searchDisabled
                  : tooltip.searchEnabled
              }
            >
              <Button
                className="text-sm font-medium bg-[#183D5D] text-white rounded-full px-6 py-4 flex items-center gap-4 shadow-xl hover:bg-[#4A90E2]"
                type="primary"
                onClick={handleSearch}
                disabled={isSearchDisabled}
              >
                {buttons.search}
              </Button>
            </Tooltip>
            <Button
              className="text-sm font-medium bg-[#183D5D] text-white rounded-full px-6 py-4 flex items-center gap-4 shadow-xl hover:bg-[#4A90E2]"
              type="default"
              onClick={handleReset}
            >
              {buttons.reset}
            </Button>
          </div>
        </Form>

        {/* Grid */}
      </div>
      <div
        ref={tableRef}
        className="bg-white text-white flex flex-col rounded-md shadow-lg max-w-[1050px] mt-4"
      >
        <div className="flex justify-end items-center h-12 p-6">
          {searchResults.length > 0 ? (
            <Popconfirm
              title="Are you sure you want to export?"
              onConfirm={() => handleExport()}
              onCancel={() => setSelectedRowKeys(new Set())}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title={exportTooltip} placement="left">
                <span>
                  <ExportButton onExport={() => {}} />
                </span>
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip
              title={filterPageLabels.tooltip.noDataToExport}
              placement="left"
            >
              <span>
                <ExportButton onExport={() => {}} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className="pl-4 pr-4 pb-4">
          <Table
            columns={columns}
            rowKey={(record) => `${record.uid}`}
            dataSource={searchResults}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: getPageSizeOptions(),
              position: ["bottomCenter"],
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              },
            }}
            // scroll={
            //   searchResults.length > 0
            //     ? { x: "max-content",y:false } // Removed y to prevent inner scroll
            //     : undefined
            // }
            scroll={{
              x: "max-content", // Always show horizontal scroll
              y: searchResults.length > 0 ? 500 : false, // Show vertical scroll if data exists
            }}
            // Removes scroll when no data
            sticky={searchResults.length > 0 ? { offsetHeader: 0 } : false}
            tableLayout="fixed"
            components={{
              header: {
                wrapper: (props) => (
                  <thead
                    {...props}
                    style={{
                      padding: "2px 4px", // Adjust padding for height
                      lineHeight: "16px",
                      height: "10px", // Controls row height
                    }}
                    className="text-white text-left border bg-[var(--color-primary)]"
                  >
                    {props.children}
                  </thead>
                ),
              },
            }}
            rowSelection={rowSelection}
            bordered
            className="border-2 color-[#183D5D] shadow-lg rounded-md w-full"
          />
        </div>
      </div>
    </>
  );
};
export default EmployeeSkillSet;
